import { useEffect, useState } from "react";
import {
  X, Search, UserPlus, Phone, UserCheck, Activity, Shuffle,
  Globe, Trophy, Loader2, User, Mail, PhoneCall, Clock,
} from "lucide-react";
import api from "../lib/api";

/**
 * Lead Journey Modal · vertical timeline of a single lead's lifecycle.
 *
 * Cross-collection view: leads_raw + leads_raw_status_history + sales +
 * leads_digital. Used by management to inspect a specific lead's history
 * — useful for post-mortem analysis of lost deals or slow responses.
 *
 * Opens via 2 entry points:
 *   1) `<LeadJourneyLauncher />` — inline search input (dashboard/scorecard)
 *   2) Direct `open(leadId)` — from ranking rows (BDC table, top campaigns)
 */
const ICON_MAP = {
  "user-plus":  UserPlus,
  "user-check": UserCheck,
  "phone":      Phone,
  "activity":   Activity,
  "shuffle":    Shuffle,
  "globe":      Globe,
  "trophy":     Trophy,
};

const TYPE_COLOR = {
  created:          { bg: "#dbeafe", ring: "#3b82f6", fg: "#1e40af" },
  digital_touch:    { bg: "#e0e7ff", ring: "#6366f1", fg: "#3730a3" },
  first_response:   { bg: "#dcfce7", ring: "#16a34a", fg: "#166534" },
  sales_assignment: { bg: "#fef3c7", ring: "#f4c400", fg: "#78350f" },
  status_change:    { bg: "#f1f5f9", ring: "#64748b", fg: "#334155" },
  reassignment:     { bg: "#fce7f3", ring: "#ec4899", fg: "#9d174d" },
  sale:             { bg: "#fef9c3", ring: "#eab308", fg: "#713f12" },
};

export function LeadJourneyModal({ leadId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!leadId) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api.get(`/leads/${leadId}/journey`);
        if (mounted) {
          if (r.data.error) setError(r.data.error);
          else setData(r.data);
        }
      } catch (e) {
        if (mounted) setError(e.message || "network_error");
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [leadId]);

  if (!leadId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
      style={{ background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", padding: "40px 20px" }}
      onClick={onClose}
      data-testid="lead-journey-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()} style={{ minHeight: 200 }}>

        {/* Header */}
        <div className="flex items-start justify-between p-5"
          style={{ borderBottom: "1px solid #f1f5f9" }}>
          <div>
            <h2 className="font-display font-extrabold text-lg"
              style={{ letterSpacing: "-0.02em" }}>
              Lead Journey
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Timeline completo · cross-collection (CRM · Digital · Sales)
            </p>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100"
            data-testid="journey-close">
            <X size={18} className="text-gray-500"/>
          </button>
        </div>

        {loading && (
          <div className="p-8 flex items-center justify-center text-gray-400">
            <Loader2 size={20} className="animate-spin mr-2"/> Cargando timeline...
          </div>
        )}
        {error && (
          <div className="p-8 text-center text-red-600 font-bold">
            {error === "lead_not_found" ? "Lead no encontrado" : `Error: ${error}`}
          </div>
        )}

        {data && !loading && (
          <>
            {/* Lead identity card */}
            <div className="p-5" style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <div className="flex items-start gap-4">
                <div style={{
                  width: 48, height: 48, borderRadius: 24,
                  background: "#f4c400", color: "#111827",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 800, fontSize: 18, fontFamily: "Manrope, sans-serif",
                  flexShrink: 0,
                }}>
                  {(data.lead.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-base text-gray-900 truncate">{data.lead.name || "Sin nombre"}</div>
                  <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-gray-600">
                    {data.lead.email && <span className="flex items-center gap-1"><Mail size={11}/>{data.lead.email}</span>}
                    {data.lead.phone && <span className="flex items-center gap-1"><PhoneCall size={11}/>{data.lead.phone}</span>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="pill" style={{
                      background: data.lead.status === "Abierto" ? "#fee2e2"
                        : data.lead.status === "Calificado" ? "#dcfce7" : "#f1f5f9",
                      color: data.lead.status === "Abierto" ? "#991b1b"
                        : data.lead.status === "Calificado" ? "#166534" : "#334155",
                      padding: "2px 8px", fontSize: 10, fontWeight: 800,
                    }}>
                      {data.lead.status}
                    </span>
                    {data.lead.brand && (
                      <span className="pill" style={{ background: "#fff", border: "1px solid #cbd5e1",
                        color: "#334155", padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>
                        {data.lead.brand}
                      </span>
                    )}
                    {data.lead.origin && (
                      <span className="pill" style={{ background: "#e0e7ff", color: "#3730a3",
                        padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>
                        {data.lead.origin}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Response metrics summary */}
              {(data.lead.first_response_hours != null || data.lead.first_response_owner) && (
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <MetricPill icon={Clock} label="First Response" value={
                    data.lead.first_response_hours != null
                      ? fmtHours(data.lead.first_response_hours)
                      : "—"
                  } color="#16a34a"/>
                  <MetricPill icon={User} label="BDC" value={data.lead.first_response_owner || "—"} color="#0ea5e9"/>
                  <MetricPill icon={UserCheck} label="Sales" value={data.lead.sales_assigned_owner || data.lead.current_owner || "—"} color="#f97316"/>
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className="p-5" data-testid="journey-timeline">
              {data.events.length === 0 && (
                <p className="text-center text-gray-400 py-8 text-sm">No hay eventos registrados</p>
              )}
              {data.events.map((ev, i) => {
                const Icon = ICON_MAP[ev.icon] || Activity;
                const color = TYPE_COLOR[ev.type] || TYPE_COLOR.status_change;
                const isLast = i === data.events.length - 1;
                return (
                  <div key={i} className="flex gap-3" data-testid={`journey-event-${i}`}>
                    <div className="flex flex-col items-center" style={{ flexShrink: 0 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 16,
                        background: color.bg, color: color.fg,
                        border: `2px solid ${color.ring}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, zIndex: 1,
                      }}>
                        <Icon size={14}/>
                      </div>
                      {!isLast && (
                        <div style={{
                          width: 2, flex: 1, background: "#e2e8f0", minHeight: 30, marginTop: -1,
                        }}/>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="font-bold text-sm text-gray-900">{ev.title}</div>
                      {ev.detail && <div className="text-[11px] text-gray-600 mt-0.5">{ev.detail}</div>}
                      {ev.amount != null && (
                        <div className="text-[12px] font-extrabold mt-1" style={{ color: "#166534" }}>
                          RD${new Intl.NumberFormat("es-DO").format(Math.round(ev.amount))}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 mt-1 text-[10px] text-gray-500">
                        <span>{fmtDate(ev.at)}</span>
                        {ev.actor && <span>· {ev.actor}</span>}
                        {ev.hours_from_created != null && (
                          <span>· {fmtHours(ev.hours_from_created)} desde recepción</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Search input that opens the journey modal on click. Drop into any page
 * (dashboard, scorecard, admin) as a 1-liner.
 */
export function LeadJourneyLauncher({ testId = "lead-journey-launcher" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!q || q.trim().length < 3) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/leads/search", { params: { q } });
        setResults(r.data.results || []);
        setOpen(true);
      } finally { setSearching(false); }
    }, 250);   // debounce
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div className="relative" data-testid={testId}>
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white"
        style={{ border: "1px solid #e2e8f0" }}>
        <Search size={14} className="text-gray-400"/>
        <input value={q} onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.length >= 3 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Buscar lead por nombre, email o teléfono..."
          className="flex-1 text-xs bg-transparent outline-none placeholder-gray-400"
          style={{ minWidth: 260 }}
          data-testid={`${testId}-input`} />
        {searching && <Loader2 size={12} className="animate-spin text-gray-400"/>}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-40 mt-1 bg-white rounded-lg shadow-lg w-full"
          style={{ border: "1px solid #e2e8f0", maxHeight: 320, overflowY: "auto" }}>
          {results.map((r) => (
            <button key={r.lead_id}
              onClick={() => { setSelected(r.lead_id); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-50"
              data-testid={`journey-search-result-${r.lead_id}`}
              style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}>
              <div className="font-bold text-xs text-gray-900 truncate">{r.name || "Sin nombre"}</div>
              <div className="flex flex-wrap gap-2 text-[10px] text-gray-500 mt-0.5">
                {r.email && <span>{r.email}</span>}
                {r.phone && <span>· {r.phone}</span>}
                <span className="pill" style={{
                  background: r.status === "Abierto" ? "#fee2e2" : "#f1f5f9",
                  color: r.status === "Abierto" ? "#991b1b" : "#334155",
                  padding: "0 6px", fontSize: 9, fontWeight: 800,
                }}>{r.status}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      {selected && <LeadJourneyModal leadId={selected} onClose={() => setSelected(null)}/>}
    </div>
  );
}

function MetricPill({ icon: Icon, label, value, color }) {
  return (
    <div className="p-2 rounded-md bg-white" style={{ border: "1px solid #e2e8f0" }}>
      <div className="text-[9px] font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1">
        <Icon size={9} style={{ color }}/>
        {label}
      </div>
      <div className="text-xs font-bold text-gray-900 truncate mt-0.5" title={value}>{value}</div>
    </div>
  );
}

function fmtHours(h) {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}min`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = typeof s === "string" ? new Date(s) : s;
    return d.toLocaleString("es-DO", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(s); }
}
